import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Avatar,
    Box,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Typography
} from '@mui/material';
import {getPaginatedSports, saveSport} from '../Api';
import {useNavigate} from 'react-router-dom';
import {DataGrid} from '@mui/x-data-grid/DataGrid/DataGrid';
import {GridColDef} from '@mui/x-data-grid/models/colDef/gridColDef';
import {GridCellParams} from '@mui/x-data-grid/models/params/gridCellParams';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import SportEntityDto from "../models/dtos/SportEntityDto";
import {StrapiFolder} from "../models/dtos/StrapiFolder";


export default function SportsPage() {
    let defaultLimit = 25;
    let before = new Date();
    let after = new Date();
    before.setDate(before.getDate() + 7);
    after.setDate(after.getDate() - 7);

    const [state, setState] = React.useState({
        before: before,
        after: after,
        loaded: false,
        currentPage: 0,
        limit: defaultLimit,
        totalPages: 0,
        matchState: 1,
        beforeDisplay: before,
        afterDisplay: after,
        searchTerm: undefined as string | undefined,
        sports: [] as SportEntityDto[],
        images: {} as StrapiFolder
    });
    const [editingRowId, setEditingRowId] = React.useState<number | null>(null);

    const [providerSyncLoading, setProviderSyncLoading] = React.useState(false);

    const navigate = useNavigate();

    const onSportEditClicked = async (sport: SportEntityDto) => {
        if (editingRowId === sport.id) {
            // Save logic here (e.g., call API or update state)
            await saveSport(sport);
            setEditingRowId(null);
        } else {
            setEditingRowId(sport.id);
        }
    }

    const onImageSelected = (sportId: number, imageId: number) => {
        let image = state.images.files?.find(x => x.id === imageId)
        let url = image?.url ?? null;
        if (url == null) {
            return
        }
        //     set to sport imageurl
        let sport = state.sports.find(x => x.id === sportId) ?? null;
        if (sport == null) {
            return
        }
        sport.background_url = url;
        setState(prevState => ({
            ...prevState,
            sports: prevState.sports.map(s => s.id === sportId ? sport! : s)
        }));
    }

    const columns: GridColDef[] = [
        {field: 'id', headerName: 'id', width: 128},
        {
            field: 'sport.photo', headerName: 'Sport', width: 100, renderCell: (params: GridCellParams) =>
                <React.Fragment><Avatar
                    src={params.row?.photo_url}
                    sx={{width: 40, height: 40}}
                    title={params.row?.name}
                /></React.Fragment>
        },
        {
            field: 'sport.photoBackground',
            headerName: 'Sport Background',
            width: 350,
            renderCell: (params: GridCellParams) => (
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                        variant="square"
                        src={`${process.env.REACT_APP_STRAPI_API_URL}${params.row?.background_url}`}
                        sx={{width: 200, height: 140}}
                        imgProps={{style: {objectFit: 'contain'}}}
                        title={params.row?.name}
                    />
                    {editingRowId === params.row.id && (
                        <FormControl sx={{minWidth: 120}}>
                            <InputLabel id={`select-image-label-${params.row.id}`}>Select Image</InputLabel>
                            <Select
                                labelId={`select-image-label-${params.row.id}`}
                                value={state.images.files?.find(x => x.url === params.row?.background_url)?.id ?? ''}
                                onChange={e => onImageSelected(params.row.id, e.target.value as number)}
                                label="Select Image"
                                size="small"
                            >
                                {(state.images.files ?? []).map(x => (
                                    <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            ),
        },
        {
            field: 'name', headerName: 'Name', width: 150, renderCell: (params: GridCellParams) =>
                <React.Fragment>{params.row?.name}</React.Fragment>
        },
        {
            field: 'actions', headerName: 'Actions', width: 250, renderCell: (params: GridCellParams) =>
                <React.Fragment><Button variant="contained" fullWidth={true} style={{height: '32px'}}
                                        onClick={() => onSportEditClicked(params.row)}>Edit</Button></React.Fragment>
        },
    ];


    const fetchData = async (currentPage: number, limit: number, before: Date, after: Date) => {
        setState(prevState => ({...prevState, loaded: false}));
        let responseSports = await getPaginatedSports(currentPage * limit, limit)

        let sports = responseSports?.items ?? [];
        let totalItems = responseSports?.totalItems ?? 0;
        const totalPages = Math.ceil(totalItems / limit)
        setState(prevState => ({
            ...prevState,
            before: before,
            after: after,
            sports: sports,
            loaded: true,
            totalPages: totalPages,
            currentPage: currentPage
        }));

        let [images] = await Promise.all([fetchCompetitionImages()]);
    }


    async function fetchCompetitionImages(): Promise<StrapiFolder> {
        const res = await fetch(`${process.env.REACT_APP_STRAPI_API_URL}/file-system/docs/Sports`, {
            headers: {
                'Authorization': `${process.env.REACT_APP_STRAPI_API_AUTH_TOKEN}`,
            },
        });
        const folder = await res.json() as StrapiFolder;
        setState(prevState => ({
            ...prevState, images: folder
        }));
        return folder;

    }

    const onPageChanged = async (p: number) => {
        await fetchData(p - 1, state.limit, state.before, state.after);
    }


    React.useEffect(() => {
        fetchData(state.currentPage, state.limit, state.before, state.after);
    }, []);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div><Stack gap="50px 50px" sx={{mx: 5, my: 5}} justifyContent='center' alignItems='center'>
                <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 4, lg: 6}} alignItems='center'>
                    <Grid item xs={0} sm={1} md={1} lg={2}/>
                    <Box width="100%"/>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Typography variant="h3">Sports</Typography>
                    </Grid>

                    {/*<Grid item xs={1} sm={1} md={1} lg={1}>*/}
                    {/*    <Button variant="contained" fullWidth={true} style={{height: '54px'}}*/}
                    {/*            onClick={() => navigate('/competitions/create')}>Create new</Button>*/}
                    {/*</Grid>*/}
                    <Box width="100%"/>
                </Grid>
                <Pagination count={state.totalPages} size="medium" onChange={(e, p) => onPageChanged(p)}
                            page={state.currentPage + 1}/>
                {!state.loaded ?
                    <Grid item xs={2}><CircularProgress size='100px' style={{marginBottom: '32px'}}/></Grid> :
                    <DataGrid sx={{width: '100%'}}
                              rowHeight={140}
                              rows={state.sports}
                              columns={columns}
                              initialState={{
                                  pagination: {
                                      paginationModel: {page: 0, pageSize: 10},
                                  },
                              }}
                              pageSizeOptions={[5, 10]}
                              hideFooterPagination

                    />}
            </Stack>
            </div>
        </LocalizationProvider>
    );
}